import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import { ConfigProvider, DatePicker, InputNumber, Modal } from 'antd';
import { ThemeProvider } from 'antd-style';
import enUS from 'antd/lib/locale/en_US';
import esES from 'antd/lib/locale/es_ES';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { FiX } from 'react-icons/fi';
import axiosInstance from '../../utils/axiosInstance';
import { getAttachmentName, getAttachmentUrl } from '../../utils/attachments';
import { toDateOnlyString } from '../../utils/dateOnly';
import { translateApiMessage } from '../../utils/translateApiMessage';
import { Button } from '../ui/Button';
import { confirmAction } from '../ui/ConfirmDialog';
import { FormField, inputClassName } from '../ui/FormField';
import { AppTooltip } from '../ui/AppTooltip';
import { selectStyles } from '../ui/selectStyles';
import { TransactionAttachmentField } from './TransactionAttachmentField';
import { isAdmin, isCustomer } from '../../utils/permissions';
import { useSession } from '../../context/SessionContext';
import {
  clearBatchDraft,
  clearSingleTransactionDraft,
  createBatchId,
  duplicateBatchItem as createDuplicateBatchItem,
  getBatchDraftKey,
  getSingleTransactionDraftKey,
  loadBatchDraft,
  loadSingleTransactionDraft,
  removeBatchItem,
  saveBatchDraft,
  saveSingleTransactionDraft,
  summarizeBatch,
  upsertBatchItem,
  validateBatchItem,
} from '../../utils/transactionBatch';
import { TransactionBatchQueue } from './TransactionBatchQueue';

const MAX_ATTACHMENTS = 25;
const ACCEPTED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
export const attachmentToFile = async (attachment) => {
  const response = await fetch(getAttachmentUrl(attachment), {
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Unable to prepare existing attachment');
  const blob = await response.blob();
  return new File([blob], getAttachmentName(attachment), {
    type: blob.type || 'application/octet-stream',
    lastModified: Date.now(),
  });
};

const emptyForm = () => ({
  customerId: null,
  supplierId: null,
  transactionType: null,
  amount: 0,
  document: '',
  notes: '',
  date: null,
});

const toBatchItem = (formData, files, id = createBatchId()) => ({
  id,
  customerId: formData.customerId || '',
  supplierId: formData.supplierId || '',
  transactionType: formData.transactionType || '',
  amount: Number(formData.amount) || 0,
  document: formData.document || '',
  notes: formData.notes || '',
  date: toDateOnlyString(formData.date),
  newFiles: files || [],
});

const toEditorForm = (item) => ({
  customerId: item.customerId || null,
  supplierId: item.supplierId || null,
  transactionType: item.transactionType || null,
  amount: Number(item.amount) || 0,
  document: item.document || '',
  notes: item.notes || '',
  date: item.date ? dayjs(item.date) : null,
});

const hasEditorData = (formData, files) => Boolean(
  formData.customerId
  || formData.supplierId
  || formData.transactionType
  || formData.date
  || Number(formData.amount) > 0
  || formData.document
  || formData.notes
  || files.length
);

const sameTransactionValue = (left, right) => String(left ?? '').trim() === String(right ?? '').trim();

export const createTransactionSnapshot = (formData) => ({
  customerId: formData.customerId ?? '',
  supplierId: formData.supplierId ?? '',
  transactionType: formData.transactionType ?? '',
  amount: Number(formData.amount || 0),
  document: formData.document ?? '',
  notes: formData.notes ?? '',
  date: toDateOnlyString(formData.date),
});

const transactionToSnapshot = (transaction) => createTransactionSnapshot({
  customerId: transaction?.customer,
  supplierId: transaction?.supplier,
  transactionType: transaction?.transaction,
  amount: transaction?.amount,
  document: transaction?.document,
  notes: transaction?.note,
  date: transaction?.date,
});

export const isAttachmentOnlyEdit = ({ transaction, originalFormData, formData, existingAttachments, retainedAttachments, newFiles }) => {
  const original = originalFormData || transactionToSnapshot(transaction);
  if (!transaction && !originalFormData) return false;
  const attachmentsChanged = newFiles.length > 0
    || retainedAttachments.length !== existingAttachments.length
    || retainedAttachments.some((attachment) => !existingAttachments.includes(attachment));
  if (!attachmentsChanged) return false;
  return sameTransactionValue(original.customerId, formData.customerId)
    && sameTransactionValue(original.supplierId, formData.supplierId)
    && sameTransactionValue(original.transactionType, formData.transactionType)
    && Number(original.amount || 0) === Number(formData.amount || 0)
    && sameTransactionValue(original.document, formData.document)
    && sameTransactionValue(original.notes, formData.notes)
    && original.date === toDateOnlyString(formData.date);
};

const TransactionForm = ({
  customers,
  suppliers,
  showTransactionForm,
  setShowTransactionForm,
  type,
  onClose,
  transaction,
  setIsChanged,
  isChanged,
  transactionId,
  setLoading,
  transactionApproveStatus,
  userRole,
}) => {
  const { t, i18n } = useTranslation();
  const { user } = useSession();
  const isDarkMode = useSelector((state) => state.darkMode.isDarkMode);
  const [formData, setFormData] = useState(emptyForm);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [retainedAttachments, setRetainedAttachments] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [batchMode, setBatchMode] = useState(false);
  const [batchItems, setBatchItems] = useState([]);
  const [editingBatchId, setEditingBatchId] = useState(null);
  const [idempotencyKey, setIdempotencyKey] = useState(createBatchId);
  const [draftStatus, setDraftStatus] = useState('');
  const [draftChecked, setDraftChecked] = useState(false);
  const [singleDraftStatus, setSingleDraftStatus] = useState('');
  const [singleDraftChecked, setSingleDraftChecked] = useState(false);
  const [isModeTransitioning, setIsModeTransitioning] = useState(false);
  const [isModalExpanded, setIsModalExpanded] = useState(false);
  const skipDraftSaveRef = useRef(false);
  const skipSingleDraftSaveRef = useRef(false);
  const editSnapshotRef = useRef(null);
  const flipTimersRef = useRef([]);
  const amountInputRef = useRef(null);
  const locale = i18n.language === 'en' ? enUS : esES;
  const draftKey = getBatchDraftKey(user);
  const singleDraftKey = getSingleTransactionDraftKey(user);
  const batchSummary = useMemo(() => summarizeBatch(batchItems), [batchItems]);

  useEffect(() => () => {
    flipTimersRef.current.forEach((timer) => window.clearTimeout(timer));
  }, []);

  useEffect(() => {
    if (!showTransactionForm) return;
    if (type === 'Edit' && transaction) {
      const attachments = transaction.attachments || [];
      const editForm = {
        customerId: transaction.customer || '',
        supplierId: transaction.supplier || '',
        transactionType: transaction.transaction || '',
        amount: transaction.amount || '',
        document: transaction.document || '',
        notes: transaction.note || '',
        date: dayjs(toDateOnlyString(transaction.date)),
      };
      editSnapshotRef.current = createTransactionSnapshot(editForm);
      setFormData(editForm);
      setExistingAttachments(attachments);
      setRetainedAttachments(attachments);
      setNewFiles([]);
      return;
    }
    editSnapshotRef.current = null;
    setFormData(emptyForm());
    setExistingAttachments([]);
    setRetainedAttachments([]);
    setNewFiles([]);
  }, [showTransactionForm, transaction, type]);

  useEffect(() => {
    if (!showTransactionForm || type !== 'Add' || batchMode || !singleDraftKey || singleDraftChecked) return;
    let active = true;
    loadSingleTransactionDraft(singleDraftKey).then((draft) => {
      if (!active) return;
      setSingleDraftChecked(true);
      if (!draft || !hasEditorData(draft, draft.newFiles || [])) return;
      confirmAction({
        title: t('Recover transaction draft?'),
        content: t('A saved individual transaction was found for this account.'),
        okText: t('Recover draft'),
        cancelText: t('Discard draft'),
        tone: 'info',
        onOk: () => {
          setFormData(toEditorForm(draft));
          setNewFiles(draft.newFiles || []);
          setSingleDraftStatus('saved');
        },
        onCancel: () => clearSingleTransactionDraft(singleDraftKey),
      });
    });
    return () => { active = false; };
  }, [batchMode, showTransactionForm, singleDraftChecked, singleDraftKey, t, type]);

  useEffect(() => {
    if (!showTransactionForm || type !== 'Add' || !batchMode || !isAdmin(userRole) || !draftKey || draftChecked) return;
    let active = true;
    loadBatchDraft(draftKey).then((draft) => {
      if (!active) return;
      setDraftChecked(true);
      if (!draft || (!draft.items.length && !hasEditorData(draft.editor || emptyForm(), draft.editor?.newFiles || []))) return;
      confirmAction({
        title: t('Recover transaction batch?'),
        content: t('A saved draft was found for this account.'),
        okText: t('Recover draft'),
        cancelText: t('Discard draft'),
        tone: 'info',
        onOk: () => {
          setBatchMode(true);
          setIsModalExpanded(true);
          setBatchItems(draft.items || []);
          setIdempotencyKey(draft.idempotencyKey || createBatchId());
          setFormData(toEditorForm(draft.editor || emptyForm()));
          setNewFiles(draft.editor?.newFiles || []);
          setEditingBatchId(
            draft.editor?.id && draft.editor.id !== 'editor' && draft.items.some((item) => item.id === draft.editor.id)
              ? draft.editor.id
              : null,
          );
          setDraftStatus('saved');
        },
        onCancel: () => clearBatchDraft(draftKey),
      });
    });
    return () => { active = false; };
  }, [batchMode, draftChecked, draftKey, showTransactionForm, t, type, userRole]);

  useEffect(() => {
    if (!showTransactionForm || type !== 'Add' || batchMode || !singleDraftChecked || !singleDraftKey || skipSingleDraftSaveRef.current) return undefined;
    if (!hasEditorData(formData, newFiles)) {
      setSingleDraftStatus('');
      clearSingleTransactionDraft(singleDraftKey).catch(() => {});
      return undefined;
    }
    setSingleDraftStatus('saving');
    const timeout = window.setTimeout(() => {
      saveSingleTransactionDraft(singleDraftKey, toBatchItem(formData, newFiles, 'single-editor'))
        .then(() => setSingleDraftStatus('saved'))
        .catch(() => setSingleDraftStatus('error'));
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [batchMode, formData, newFiles, showTransactionForm, singleDraftChecked, singleDraftKey, type]);

  useEffect(() => {
    if (!batchMode || !draftChecked || !draftKey || skipDraftSaveRef.current) return undefined;
    setDraftStatus('saving');
    const timeout = window.setTimeout(() => {
      saveBatchDraft(draftKey, {
        idempotencyKey,
        items: batchItems,
        editor: toBatchItem(formData, newFiles, editingBatchId || 'editor'),
      })
        .then(() => setDraftStatus('saved'))
        .catch(() => setDraftStatus('error'));
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [batchItems, batchMode, draftChecked, draftKey, editingBatchId, formData, idempotencyKey, newFiles]);

  const resetForm = () => {
    editSnapshotRef.current = null;
    setFormData(emptyForm());
    setExistingAttachments([]);
    setRetainedAttachments([]);
    setNewFiles([]);
  };

  const finishClose = () => {
    flipTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    flipTimersRef.current = [];
    setIsModeTransitioning(false);
    setShowTransactionForm(false);
    onClose?.();
    resetForm();
    setEditingBatchId(null);
    setDraftChecked(false);
    setSingleDraftChecked(false);
    setSingleDraftStatus('');
    if (!batchItems.length) {
      setBatchMode(false);
      setIsModalExpanded(false);
    }
  };

  const closeForm = () => {
    if (batchMode && (batchItems.length || hasEditorData(formData, newFiles))) {
      confirmAction({
        title: t('Close transaction batch?'),
        content: t('Your draft is saved and can be recovered when you return.'),
        okText: t('Close'),
        cancelText: t('Continue editing'),
        tone: 'warning',
        onOk: finishClose,
      });
      return;
    }
    if (type === 'Add' && hasEditorData(formData, newFiles)) {
      confirmAction({
        title: t('Close transaction draft?'),
        content: t('Your draft is saved and can be recovered when you return.'),
        okText: t('Close'),
        cancelText: t('Continue editing'),
        tone: 'warning',
        onOk: finishClose,
      });
      return;
    }
    finishClose();
  };

  const handleChange = ({ target, name, value }) => {
    const fieldName = target?.name || name;
    const fieldValue = target?.value ?? value;
    setFormData((current) => ({ ...current, [fieldName]: fieldValue }));
  };

  const addFiles = (files) => {
    const validFiles = files.filter((file) => ACCEPTED_FILE_TYPES.includes(file.type) || /\.(jpe?g|png|webp|jfif|pdf)$/i.test(file.name));
    if (validFiles.length !== files.length) toast.warning(t('Some files have an unsupported format'));
    const available = MAX_ATTACHMENTS - retainedAttachments.length - newFiles.length;
    if (validFiles.length > available) toast.warning(t('A maximum of 25 attachments is allowed'));
    setNewFiles((current) => [...current, ...validFiles.slice(0, Math.max(available, 0))]);
  };

  const appendAttachments = async (payload) => {
    if (type === 'Add') {
      newFiles.forEach((file) => payload.append('attachments', file));
      payload.append('isRemove', false);
      return;
    }

    const attachmentsChanged = retainedAttachments.length !== existingAttachments.length || newFiles.length > 0;
    if (!attachmentsChanged) {
      payload.append('isRemove', false);
      return;
    }

    if (retainedAttachments.length + newFiles.length === 0) {
      payload.append('isRemove', true);
      return;
    }

    const retainedFiles = await Promise.all(retainedAttachments.map(attachmentToFile));
    [...retainedFiles, ...newFiles].forEach((file) => payload.append('attachments', file));
    payload.append('isRemove', false);
  };

  const resetEditorWithRecentValues = (item) => {
    setFormData({
      customerId: item.customerId || null,
      supplierId: item.supplierId || null,
      transactionType: item.transactionType || null,
      amount: 0,
      document: '',
      notes: '',
      date: item.date ? dayjs(item.date) : null,
    });
    setNewFiles([]);
    setEditingBatchId(null);
    window.setTimeout(() => amountInputRef.current?.focus?.(), 0);
  };

  const addOrUpdateBatchItem = () => {
    const item = toBatchItem(formData, newFiles, editingBatchId || createBatchId());
    const validation = validateBatchItem(item);
    if (!validation.valid) {
      toast.error(t('Complete the required fields before adding this transaction.'));
      return false;
    }
    setBatchItems((current) => upsertBatchItem(current, item, editingBatchId));
    resetEditorWithRecentValues(item);
    return true;
  };

  const editBatchItem = (item) => {
    setEditingBatchId(item.id);
    setFormData(toEditorForm(item));
    setNewFiles(item.newFiles || []);
  };

  const duplicateBatchItem = (item) => {
    const duplicate = createDuplicateBatchItem(item);
    setBatchItems((current) => [...current, duplicate]);
    editBatchItem(duplicate);
  };

  const deleteBatchItem = (id) => {
    setBatchItems((current) => removeBatchItem(current, id));
    if (editingBatchId === id) resetForm();
    if (editingBatchId === id) setEditingBatchId(null);
  };

  const discardBatch = async () => {
    skipDraftSaveRef.current = true;
    await clearBatchDraft(draftKey);
    setBatchItems([]);
    setEditingBatchId(null);
    setIdempotencyKey(createBatchId());
    setDraftStatus('');
    resetForm();
    window.setTimeout(() => { skipDraftSaveRef.current = false; }, 0);
  };

  const animateModeChange = (enabled, applyMode, { onMidpoint, onComplete } = {}) => {
    if (isModeTransitioning || batchMode === enabled) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      applyMode();
      setIsModalExpanded(enabled);
      onMidpoint?.();
      onComplete?.();
      return;
    }

    flipTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    setIsModeTransitioning(true);
    applyMode();
    flipTimersRef.current = [
      window.setTimeout(() => {
        setIsModalExpanded(enabled);
        onMidpoint?.();
      }, 513),
      window.setTimeout(() => {
        setIsModeTransitioning(false);
        onComplete?.();
      }, 1080),
    ];
  };

  const confirmLargeBatch = () => new Promise((resolve) => {
    if (batchItems.length <= 10) return resolve(true);
    confirmAction({
      title: t('Save transaction batch?'),
      content: t('You are about to create {{count}} transactions.', { count: batchItems.length }),
      okText: t('Save batch'),
      cancelText: t('Cancel'),
      tone: 'info',
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });

  const saveBatch = async () => {
    if (isSubmitting) return;
    if (!batchItems.length) {
      toast.warning(t('Add at least one transaction to the batch.'));
      return;
    }
    const summary = summarizeBatch(batchItems);
    if (summary.invalid) {
      toast.error(t('Review incomplete or duplicated transactions before saving.'));
      return;
    }
    if (!(await confirmLargeBatch())) return;

    setIsSubmitting(true);
    setLoading(true);
    try {
      const payload = new FormData();
      payload.append('transactions', JSON.stringify(batchItems.map((item) => ({
        customer_id: item.customerId,
        supplier_id: item.supplierId,
        transaction_type: item.transactionType,
        amount: item.amount,
        document: item.document,
        notes: item.notes,
        transaction_date: item.date,
      }))));
      batchItems.forEach((item, index) => {
        (item.newFiles || []).forEach((file) => payload.append(`attachments_${index}`, file));
      });
      const response = await axiosInstance('/transaction/create_batch', 'post', payload, {
        headers: { 'Idempotency-Key': idempotencyKey },
      });
      if (!response || response.status >= 400) {
        const serverErrors = response?.data?.errors || [];
        if (serverErrors.length) {
          setBatchItems((current) => current.map((item, index) => ({
            ...item,
            serverError: serverErrors.find((error) => error.index === index)?.message || '',
          })));
        }
        throw new Error(response?.data?.message || t('Unable to create transaction batch.'));
      }
      skipDraftSaveRef.current = true;
      await clearBatchDraft(draftKey);
      await clearSingleTransactionDraft(singleDraftKey);
      toast.success(t('{{count}} transactions were created successfully.', { count: response.data.count }));
      setBatchItems([]);
      setBatchMode(false);
      setIsModalExpanded(false);
      setIdempotencyKey(createBatchId());
      setIsChanged(!isChanged);
      finishClose();
    } catch (error) {
      toast.error(translateApiMessage(t, error.message, 'Something went wrong'));
    } finally {
      setLoading(false);
      setIsSubmitting(false);
      window.setTimeout(() => { skipDraftSaveRef.current = false; }, 0);
    }
  };

  const toggleBatchMode = (enabled) => {
    if (enabled) {
      animateModeChange(true, () => {
        setBatchMode(true);
        setDraftChecked(false);
        setIdempotencyKey((current) => current || createBatchId());
      });
      return;
    }
    if (!batchItems.length) {
      animateModeChange(false, () => {
        skipDraftSaveRef.current = true;
        clearBatchDraft(draftKey).catch(() => {});
        setDraftStatus('');
        setBatchMode(false);
        setSingleDraftChecked(true);
        window.setTimeout(() => { skipDraftSaveRef.current = false; }, 0);
      });
      return;
    }
    confirmAction({
      title: t('Discard transaction batch?'),
      content: t('This removes the saved draft and all queued transactions.'),
      okText: t('Discard draft'),
      cancelText: t('Cancel'),
      danger: true,
      onOk: () => {
        skipSingleDraftSaveRef.current = true;
        animateModeChange(false, () => {
          setBatchMode(false);
          setSingleDraftChecked(true);
        }, {
          onMidpoint: () => discardBatch(),
          onComplete: () => {
            skipSingleDraftSaveRef.current = false;
            setSingleDraftChecked(false);
          },
        });
      },
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;
    if (!isAdmin(userRole) && !isCustomer(userRole)) {
      toast.error(t('You do not have permission to perform this action'));
      return;
    }
    if (batchMode && type === 'Add') {
      addOrUpdateBatchItem();
      return;
    }
    if (!formData.customerId || !formData.supplierId || !formData.transactionType || !formData.date) {
      toast.error(t('Please fill all fields'));
      return;
    }

    setIsSubmitting(true);
    try {
      const attachmentOnlyUpdate = type === 'Edit' && isAttachmentOnlyEdit({
        transaction,
        originalFormData: editSnapshotRef.current,
        formData,
        existingAttachments,
        retainedAttachments,
        newFiles,
      });

      if (attachmentOnlyUpdate) {
        const attachmentPayload = new FormData();
        attachmentPayload.append('transaction_id', transactionId || '');
        attachmentPayload.append('retained_attachments', JSON.stringify(retainedAttachments));
        newFiles.forEach((file) => attachmentPayload.append('attachments', file));
        setLoading(true);
        const response = await axiosInstance('/transaction/update_attachments', 'put', attachmentPayload);
        if (!response || response.status >= 400) throw new Error(response?.data?.message || t('Something went wrong'));
        toast.success(t('Transaction attachments updated successfully'));
        setIsChanged(!isChanged);
        finishClose();
        return;
      }

      const payload = new FormData();
      payload.append('customer_id', formData.customerId);
      payload.append('supplier_id', formData.supplierId);
      payload.append('transaction_type', formData.transactionType);
      payload.append('amount', formData.amount);
      payload.append('document', formData.document);
      payload.append('notes', formData.notes);
      payload.append('transaction_id', transactionId || '');
      payload.append('transaction_date', toDateOnlyString(formData.date));
      await appendAttachments(payload);

      if (isAdmin(userRole)) {
        setLoading(true);
        if (type === 'Add') {
          const response = await axiosInstance('/transaction/create_transaction', 'post', payload);
          if (!response || response.status >= 400) throw new Error(response?.data?.message || t('Something went wrong'));
          toast.success(t('Transaction created successfully'));
        } else {
          const endpoint = transactionApproveStatus === 2
            ? '/transaction/update_create_transaction'
            : '/transaction/update_transaction';
          const response = await axiosInstance(endpoint, 'put', payload);
          if (!response || response.status >= 400) throw new Error(response?.data?.message || t('Something went wrong'));
          toast.success(t('Transaction updated successfully'));
        }
      } else {
        if (transactionApproveStatus === 2 || transactionApproveStatus === 3) {
          toast.warning(t('This transaction needs to be approved'));
          return;
        }
        setLoading(true);
        payload.append('original_transaction', transactionId);
        const response = await axiosInstance('/pending/create_pending_transaction', 'post', payload);
        if (!response || response.status >= 400) throw new Error(response?.data?.message || t('Something went wrong'));
        toast.success(t('Transaction pending successfully'));
      }

      if (type === 'Add') {
        skipSingleDraftSaveRef.current = true;
        await clearSingleTransactionDraft(singleDraftKey);
        setSingleDraftStatus('');
      }
      setIsChanged(!isChanged);
      finishClose();
    } catch (error) {
      toast.error(translateApiMessage(t, error.response?.data?.message || error.message, 'Something went wrong'));
    } finally {
      setLoading(false);
      setIsSubmitting(false);
      window.setTimeout(() => { skipSingleDraftSaveRef.current = false; }, 0);
    }
  };

  const handleFormKeyDown = (event) => {
    if (!batchMode || type !== 'Add' || (!event.ctrlKey && !event.metaKey) || event.key !== 'Enter') return;
    event.preventDefault();
    if (!isSubmitting) addOrUpdateBatchItem();
  };

  if (!showTransactionForm) return null;

  const customerOptions = customers.map((customer) => ({ value: customer._id, label: `${customer.firstName} ${customer.lastName}` }));
  const supplierOptions = suppliers.map((supplier) => ({ value: supplier._id, label: supplier.name }));
  const transactionTypeOptions = [
    { value: 'invoice', label: t('invoice').toUpperCase() },
    { value: 'payment', label: t('payment').toUpperCase() },
  ];

  const renderSelect = (options, value, name, placeholder, disabled = false) => (
    <Select
      options={options}
      value={options.find((option) => option.value === value) || null}
      onChange={(option) => setFormData((current) => ({ ...current, [name]: option?.value || '' }))}
      styles={selectStyles}
      placeholder={placeholder}
      isDisabled={disabled || isSubmitting}
      menuPortalTarget={document.body}
      menuPosition='fixed'
    />
  );

  const canFlipModes = type === 'Add' && isAdmin(userRole);
  const hasFixedParties = type === 'Edit' && isCustomer(userRole);

  const renderModeFace = (isBatchFace) => {
    const faceIsActive = !canFlipModes || batchMode === isBatchFace;
    const activeDraftStatus = isBatchFace ? draftStatus : singleDraftStatus;

    return (
      <section
        className={`transaction-form-shell transaction-flip-face ${isBatchFace ? 'transaction-flip-face--back' : 'transaction-flip-face--front'}`}
        aria-hidden={!faceIsActive}
        ref={(node) => { if (node) node.inert = !faceIsActive; }}
      >
        <AppTooltip label={t('Close')}><button
          type='button'
          className='transaction-flip-close'
          onClick={closeForm}
          disabled={!faceIsActive || isModeTransitioning}
          aria-label={t('Close')}
        >
          <FiX aria-hidden='true' />
        </button></AppTooltip>
        <header className='border-b border-[var(--border-subtle)] px-4 py-4 sm:px-6'>
          <div className='transaction-form-heading flex flex-col justify-between gap-3 pr-8 lg:flex-row lg:items-center'>
            <div className='min-w-0'>
              <h2 className='text-xl font-bold text-[var(--text-primary)] sm:text-2xl'>{type === 'Add' ? t('Add Transaction') : t('Edit Transaction')}</h2>
              <p className='mt-1 text-sm text-[var(--text-secondary)]'>{t('Keep all fields aligned with the billing contract.')}</p>
            </div>
            {canFlipModes && (
              <div className='transaction-mode-toggle' data-mode={isBatchFace ? 'batch' : 'single'} role='group' aria-label={t('Creation mode')}>
                <span className='transaction-mode-toggle-indicator' aria-hidden='true' />
                <button type='button' onClick={() => toggleBatchMode(false)} disabled={isModeTransitioning} className={`transaction-mode-toggle-button ${!isBatchFace ? 'is-active' : ''}`} aria-pressed={!isBatchFace}>
                  {t('Single transaction')}
                </button>
                <button type='button' onClick={() => toggleBatchMode(true)} disabled={isModeTransitioning} className={`transaction-mode-toggle-button ${isBatchFace ? 'is-active' : ''}`} aria-pressed={isBatchFace}>
                  {t('Multiple transactions')}
                </button>
              </div>
            )}
          </div>
        </header>

        <form
          onSubmit={handleSubmit}
          onKeyDown={handleFormKeyDown}
          className={`transaction-form-body ${isBatchFace ? 'transaction-form-body--batch' : ''}`}
          aria-keyshortcuts={isBatchFace ? 'Control+Enter Meta+Enter' : undefined}
        >
          <div className={`transaction-mode-panel ${isBatchFace ? 'is-batch' : 'is-single'}`}>
            {type === 'Add' && (
              <div className='flex min-h-5 items-center justify-end text-xs text-[var(--text-muted)]' role='status'>
                {activeDraftStatus === 'saving'
                  ? t('Saving draft...')
                  : activeDraftStatus === 'saved'
                    ? t('Draft saved')
                    : activeDraftStatus === 'error'
                      ? t('Draft could not be saved')
                      : ''}
              </div>
            )}
            <div className={isBatchFace ? 'transaction-batch-layout grid min-h-0 gap-4 xl:grid-cols-[minmax(0,1.15fr)_minmax(500px,1fr)]' : ''}>
              <div className={`space-y-3 ${isBatchFace ? 'transaction-batch-editor' : ''}`}>
                {hasFixedParties && (
                  <p className='rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-muted)] px-3 py-2 text-xs leading-5 text-[var(--text-secondary)]' role='status'>
                    {t('You can update the date, type, amount, document, notes, and attachments. Customer and supplier remain fixed.')}
                  </p>
                )}
                <div className='transaction-fields-grid grid gap-x-4 gap-y-3 sm:grid-cols-2 xl:grid-cols-3'>
                  <FormField label={t('Date')} required>
                    <DatePicker required value={formData.date} onChange={(date) => setFormData((current) => ({ ...current, date }))} format='DD/MM/YYYY' placeholder='DD/MM/YYYY' className='w-full' size='large' disabled={!faceIsActive || isSubmitting} />
                  </FormField>
                  <FormField label={t('Customer')} required hint={hasFixedParties ? t('Customer cannot be changed in a submitted transaction.') : undefined}>
                    {renderSelect(customerOptions, formData.customerId, 'customerId', t('Select a customer'), !faceIsActive || hasFixedParties)}
                  </FormField>
                  <FormField label={t('Supplier')} required hint={hasFixedParties ? t('Supplier cannot be changed in a submitted transaction.') : undefined}>
                    {renderSelect(supplierOptions, formData.supplierId, 'supplierId', t('Select a supplier'), !faceIsActive || hasFixedParties)}
                  </FormField>
                  <FormField label={t('Transaction Type')} required>
                    {renderSelect(transactionTypeOptions, formData.transactionType, 'transactionType', t('Select a transaction type'), !faceIsActive)}
                  </FormField>
                  <FormField label={t('Amount')} required>
                    <InputNumber ref={faceIsActive ? amountInputRef : null} className='w-full' value={formData.amount} formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={(value) => (value || '').replace(/\$\s?|(,*)/g, '')} onChange={(value) => handleChange({ name: 'amount', value })} size='large' disabled={!faceIsActive || isSubmitting} />
                  </FormField>
                  <FormField label={t('Document')}>
                    <input name='document' value={formData.document} onChange={handleChange} className={inputClassName} disabled={!faceIsActive || isSubmitting} />
                  </FormField>
                  <div className='sm:col-span-2 xl:col-span-3'>
                    <FormField label={t('Notes')}>
                      <textarea name='notes' value={formData.notes} className={`${inputClassName} min-h-[64px] resize-none`} onChange={handleChange} disabled={!faceIsActive || isSubmitting} />
                    </FormField>
                  </div>
                </div>

                <TransactionAttachmentField
                  existingAttachments={existingAttachments}
                  retainedAttachments={retainedAttachments}
                  newFiles={newFiles}
                  onRemoveExisting={(attachment) => setRetainedAttachments((current) => current.filter((item) => item !== attachment))}
                  onRemoveNew={(index) => setNewFiles((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                  onAddFiles={addFiles}
                  onRestore={() => setRetainedAttachments(existingAttachments)}
                  t={t}
                  maxFiles={MAX_ATTACHMENTS}
                />

                {isBatchFace && (
                  <div className='transaction-add-to-batch sticky bottom-0 flex justify-end border-t border-[var(--border-subtle)] bg-[var(--surface-raised)] pt-3'>
                    <Button type='button' variant='primary' className='w-full sm:w-auto sm:min-w-[170px]' onClick={addOrUpdateBatchItem} disabled={!faceIsActive || isSubmitting}>
                      {editingBatchId ? t('Update batch item') : t('Add to batch')}
                    </Button>
                  </div>
                )}
              </div>

              {isBatchFace && (
                <TransactionBatchQueue items={batchItems} summary={batchSummary} customers={customers} suppliers={suppliers} editingId={editingBatchId} onEdit={editBatchItem} onDuplicate={duplicateBatchItem} onDelete={deleteBatchItem} t={t} />
              )}
            </div>
          </div>

          <footer className={`transaction-form-footer flex flex-wrap gap-3 border-t border-[var(--border-subtle)] pt-3 ${isBatchFace ? 'justify-between' : 'justify-end'}`}>
            {isBatchFace && <Button type='button' variant='ghost' onClick={discardBatch} disabled={!faceIsActive || isSubmitting}>{t('Discard draft')}</Button>}
            <div className='ml-auto flex flex-wrap justify-end gap-3'>
              <Button type='button' variant='secondary' onClick={closeForm} disabled={!faceIsActive || isSubmitting}>{t('Cancel')}</Button>
              {!isBatchFace && <Button type='submit' variant='primary' disabled={!faceIsActive || isSubmitting}>{type === 'Add' ? t('Add') : t('Edit')}</Button>}
              {isBatchFace && <Button type='button' variant='primary' loading={isSubmitting} disabled={!faceIsActive} onClick={saveBatch}>{isSubmitting ? t('Processing...') : t('Save batch')}</Button>}
            </div>
          </footer>
        </form>
      </section>
    );
  };

  return (
    <ConfigProvider locale={locale}>
      <ThemeProvider appearance={isDarkMode ? 'dark' : 'light'}>
        <Modal
          open={showTransactionForm}
          onCancel={closeForm}
          closable={false}
          footer={null}
          width={isModalExpanded && canFlipModes ? 1420 : 920}
          centered
          className={`transaction-form-modal ${isModalExpanded && canFlipModes ? 'transaction-form-modal--batch' : ''} ${batchMode && canFlipModes ? 'transaction-form-modal--flipped' : ''} ${canFlipModes ? 'transaction-form-modal--flip' : ''}`}
          destroyOnClose
        >
          <div className='transaction-flip-scene'>
            <div className='transaction-flip-card'>
              {renderModeFace(false)}
              {canFlipModes && renderModeFace(true)}
            </div>
          </div>
        </Modal>
      </ThemeProvider>
    </ConfigProvider>
  );
};

export default TransactionForm;
